import React from 'react'
import { useTranslation } from 'react-i18next'
import { Car, Loader2, MessageSquare, Phone, User } from 'lucide-react'
import { MultiPhoneInput } from '../../form/MultiPhoneInput'
import { MultiEmailInput } from '../../form/MultiEmailInput'
import { DocumentInput } from '../../form/DocumentInput'
import { CnhInput } from '../../form/CnhInput'
import { LeadEmail, LeadPhone, VehicleInterest } from '../../../types'
import { FormField } from '../../form'
import { MultiVehicleSelector } from '../../form/MultiVehicleSelector'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui'
import { LeadFormField, LeadTextareaField } from '../form'

interface BasicInfoTabProps {
  name: string
  phones: LeadPhone[]
  emails: LeadEmail[]
  document?: string
  cnh?: string
  message?: string
  vehicleInterests: VehicleInterest[]
  onNameChange: (value: string) => void
  onPhonesChange: (phones: LeadPhone[]) => void
  onEmailsChange: (emails: LeadEmail[]) => void
  onDocumentChange: (value: string, type: 'cpf' | 'cnpj') => void
  onCnhChange: (value: string) => void
  onMessageChange: (value: string) => void
  onVehicleInterestsChange: (interests: VehicleInterest[]) => void
  checkPhoneWarning: string | null
  checkDocumentWarning?: string | null
  isCheckingPhone?: boolean
  isCheckingDocument?: boolean
  nameError?: string
  phonesError?: string
}

export const BasicInfoTab: React.FC<BasicInfoTabProps> = ({
  name,
  phones,
  emails,
  document,
  cnh,
  message,
  vehicleInterests,
  onNameChange,
  onPhonesChange,
  onEmailsChange,
  onDocumentChange,
  onCnhChange,
  onMessageChange,
  onVehicleInterestsChange,
  checkPhoneWarning,
  checkDocumentWarning = null,
  isCheckingPhone = false,
  isCheckingDocument = false,
  nameError,
  phonesError,
}) => {
  const { t } = useTranslation()

  return (
    <div className="space-y-6">
      <Card className="overflow-hidden border-border/70 shadow-sm">
        <CardHeader className="border-b border-border/60 bg-muted/30 px-5 py-4">
          <div className="flex items-start gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-accent/10 text-accent">
              <User className="h-5 w-5" />
            </span>
            <div className="space-y-1">
              <CardTitle className="text-base font-semibold tracking-tight text-foreground">
                {t('leads.form.personalInfoTitle', 'Informações Pessoais')}
              </CardTitle>
              <CardDescription className="text-sm text-muted-foreground">
                {t('leads.form.personalInfoDescription', 'Identificação e dados principais do lead.')}
              </CardDescription>
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-5 p-5">
          <LeadFormField
            label={t('leads.form.fullName', 'Nome Completo')}
            required
            error={nameError}
            value={name}
            onChange={(e) => onNameChange(e.target.value)}
            placeholder={t('leads.form.fullNamePlaceholder', 'Digite o nome completo')}
            leftIcon={<User className="h-4 w-4" />}
          />

          <div className="grid gap-4 xl:grid-cols-2">
            <div className="rounded-2xl border border-border/70 bg-muted/20 p-4 shadow-sm">
              <DocumentInput
                value={document || ''}
                onChange={(value, type) => onDocumentChange(value, type)}
                label={t('leads.form.documentLabel', 'Documento')}
              />
              {isCheckingDocument && (
                <p className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  {t('leads.form.checkingDocument', 'Verificando documento...')}
                </p>
              )}
              {checkDocumentWarning && (
                <div className="mt-3 rounded-xl border border-amber-500/20 bg-amber-500/10 px-3 py-2">
                  <p className="text-sm text-amber-800 dark:text-amber-200">{checkDocumentWarning}</p>
                </div>
              )}
            </div>

            <div className="rounded-2xl border border-border/70 bg-muted/20 p-4 shadow-sm">
              <CnhInput
                value={cnh || ''}
                onChange={(value) => onCnhChange(value)}
                placeholder="00000000000"
                label={t('leads.form.cnhLabel', 'CNH')}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="overflow-hidden border-border/70 shadow-sm">
        <CardHeader className="border-b border-border/60 bg-muted/30 px-5 py-4">
          <div className="flex items-start gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-accent/10 text-accent">
              <Phone className="h-5 w-5" />
            </span>
            <div className="space-y-1">
              <CardTitle className="text-base font-semibold tracking-tight text-foreground">
                {t('leads.form.contactsTitle', 'Contatos')}
              </CardTitle>
              <CardDescription className="text-sm text-muted-foreground">
                {t('leads.form.contactsDescription', 'Telefone e e-mail para manter o acompanhamento do lead.')}
              </CardDescription>
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-4 p-5">
          <div className="grid gap-4 xl:grid-cols-2">
            <div className="rounded-2xl border border-border/70 bg-card p-4 shadow-sm">
              <FormField
                label={t('leads.form.phonesLabel', 'Telefones')}
                error={phonesError}
                required
              >
                <MultiPhoneInput
                  value={phones}
                  onChange={onPhonesChange}
                />
              </FormField>
              {isCheckingPhone && (
                <p className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  {t('leads.form.checkingPhone', 'Verificando telefone...')}
                </p>
              )}
              {checkPhoneWarning && !phonesError && (
                <div className="mt-3 rounded-xl border border-amber-500/20 bg-amber-500/10 px-3 py-2">
                  <p className="text-sm text-amber-800 dark:text-amber-200">{checkPhoneWarning}</p>
                </div>
              )}
            </div>

            <div className="rounded-2xl border border-border/70 bg-card p-4 shadow-sm">
              <FormField label={t('leads.form.emailsLabel', 'E-mails')}>
                <MultiEmailInput
                  value={emails}
                  onChange={onEmailsChange}
                />
              </FormField>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="overflow-hidden border-border/70 shadow-sm">
        <CardHeader className="border-b border-border/60 bg-muted/30 px-5 py-4">
          <div className="flex items-start gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-accent/10 text-accent">
              <Car className="h-5 w-5" />
            </span>
            <div className="space-y-1">
              <CardTitle className="text-base font-semibold tracking-tight text-foreground">
                {t('leads.form.vehicleInterestTitle', 'Busca de Veículo')}
              </CardTitle>
              <CardDescription className="text-sm text-muted-foreground">
                {t('leads.form.vehicleInterestDescription', 'Interesses do lead para direcionar o atendimento.')}
              </CardDescription>
            </div>
          </div>
        </CardHeader>

        <CardContent className="p-5">
          <div className="rounded-2xl border border-border/70 bg-muted/20 p-4 shadow-sm">
            <MultiVehicleSelector
              value={vehicleInterests}
              onChange={onVehicleInterestsChange}
            />
          </div>
        </CardContent>
      </Card>

      <Card className="overflow-hidden border-border/70 shadow-sm">
        <CardHeader className="border-b border-border/60 bg-muted/30 px-5 py-4">
          <div className="flex items-start gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-accent/10 text-accent">
              <MessageSquare className="h-5 w-5" />
            </span>
            <div className="space-y-1">
              <CardTitle className="text-base font-semibold tracking-tight text-foreground">
                {t('leads.form.initialMessageLabel', 'Mensagem Inicial')}
              </CardTitle>
              <CardDescription className="text-sm text-muted-foreground">
                {t('leads.form.messageDescription', 'Contexto inicial do atendimento enviado pelo cliente.')}
              </CardDescription>
            </div>
          </div>
        </CardHeader>

        <CardContent className="p-5">
          <LeadTextareaField
            label={t('leads.form.messageLabel', 'Mensagem')}
            value={message || ''}
            onChange={(e) => onMessageChange(e.target.value)}
            placeholder={t('leads.form.messagePlaceholder', 'Digite a mensagem inicial do cliente...')}
            rows={4}
            className="min-h-[120px] resize-none"
          />
        </CardContent>
      </Card>
    </div>
  )
}
